// src/automation/AutomationPage.js
//
// The Automation console (route /automation/:id). WBR-themed shell (Navbar +
// Footer + FunctionPage.css). Sidebar:
//
//   Cover Page   ·  Analytics
//   SECTION HEADER (e.g. Queue Status Reports)
//     task (clickable row) -> scrollable page of cards (KPI/Description/Config/Mapping)
//
// Access control: role resolved via useAutoPermissions (admin/contributor/
// viewer). Admins get an Admin entry in the navbar user menu + an Admin view.
// Viewers see everything read-only; contributors can edit only their allowed
// tasks. The logout button is intentionally not shown.

import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Navbar from "wbr/components/navbar/Navbar";
import Footer from "wbr/components/footer/Footer";
import { useUser } from "shared/context/UserContext";
import {
  getAutomationGroups,
  getTaskConfig,
  getRunSummary,
  getBootstrap,
  deleteAutomation,
  filterVisibleSections,
  setApiLogin,
} from "./api/automation_data";
import { useAutoPermissions } from "./useAutoPermissions";
import AutomationAccordionSidebar, {
  COVER_KEY,
} from "./components/AutomationAccordionSidebar";
import AutomationTaskView from "./components/AutomationTaskView";
import AutomationCover from "./components/AutomationCover";
import AutomationAdmin from "./components/AutomationAdmin";
import ActivityLogPage from "./components/ActivityLogPage";
import "wbr/function_page/FunctionPage.css";
import "./AutomationPage.css";

const ADMIN_KEY = "__admin__";
const ACTIVITY_KEY = "__activity__";

export default function AutomationPage() {
  const user = useUser();
  const login = user?.userId || "";
  // Every API call carries this login (permissions + activity log). Set during
  // render so it's in place before the first query fires.
  setApiLogin(login);
  const qc = useQueryClient();

  const [theme, setTheme] = useState("light");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeView, setActiveView] = useState(COVER_KEY);

  // ── One-shot bootstrap: a single request that primes the sidebar, run
  // summary, recent runs, permissions, and every task's config. Its results are
  // seeded into the individual query caches below, so those useQuery hooks read
  // from cache instead of each firing their own request on first load. They
  // remain as the refetch path once their data goes stale.
  const { data: boot } = useQuery({
    queryKey: ["automationBootstrap", login],
    queryFn: () => getBootstrap(login),
    enabled: !!login,
    staleTime: 2 * 60 * 1000,
  });

  // Permissions are seeded by bootstrap; defer the standalone fetch until
  // bootstrap has settled so first load makes ONE request, not two.
  const perms = useAutoPermissions({ enabled: boot !== undefined });

  useEffect(() => {
    if (!boot) return;
    if (boot.sections) qc.setQueryData(["automationGroups"], boot.sections);
    qc.setQueryData(["automationRunSummary"], boot.summaryMap || {});
    qc.setQueryData(["automationRuns"], boot.runs || []);
    if (boot.permissions) qc.setQueryData(["autoPermissions", login], boot.permissions);
    // Seed each task's config so opening a task is instant (no per-task fetch).
    if (boot.configs) {
      for (const [name, cfg] of Object.entries(boot.configs)) {
        if (cfg) qc.setQueryData(["taskConfig", name], cfg);
      }
    }
  }, [boot, qc, login]);

  // ── Task registry grouped into sections ──
  // Don't fetch groups until bootstrap has had its chance to seed this cache
  // (bootstrap returns the same sections in one call). Once seeded, this query
  // reads from cache; it only hits the network if the cache goes stale and a
  // refetch is triggered. If bootstrap errored, it falls through and fetches.
  const { data: sections = [], isLoading: sectionsFetching } = useQuery({
    queryKey: ["automationGroups"],
    queryFn: getAutomationGroups,
    enabled: !!login && boot !== undefined,
    staleTime: 2 * 60 * 1000,
  });
  const sectionsLoading = sectionsFetching && sections.length === 0;

  // Sidebar/cover show only visible tasks; Admin uses the full list.
  const visibleSections = useMemo(() => filterVisibleSections(sections), [sections]);

  const onCover = activeView === COVER_KEY;
  const onAdmin = activeView === ADMIN_KEY;
  const onActivity = activeView === ACTIVITY_KEY && perms.canViewActivity;
  const activeTaskName = onCover || onAdmin || activeView === ACTIVITY_KEY ? null : activeView;

  // Per-task run summary (Total runs / Time saved KPIs).
  const { data: runSummary = {} } = useQuery({
    queryKey: ["automationRunSummary"],
    queryFn: getRunSummary,
    staleTime: 2 * 60 * 1000,
  });

  const activeTaskLabel = useMemo(() => {
    if (!activeTaskName) return null;
    for (const s of sections) {
      const t = (s.tasks || []).find((x) => x.name === activeTaskName);
      if (t) return t.label;
    }
    return null;
  }, [sections, activeTaskName]);

  const { data: taskConfig, isLoading: configLoading } = useQuery({
    queryKey: ["taskConfig", activeTaskName],
    queryFn: () => getTaskConfig(activeTaskName),
    enabled: !!activeTaskName,
    // Config only changes via an admin save (which invalidates this key), so a
    // long stale window is safe and avoids refetching on every task switch.
    staleTime: 10 * 60 * 1000,
  });

  const showCover = () => setActiveView(COVER_KEY);
  const showAdmin = () => {
    if (perms.canAdmin) setActiveView(ADMIN_KEY);
  };
  const showActivity = () => {
    if (perms.canViewActivity) setActiveView(ACTIVITY_KEY);
  };
  const openTask = (name) =>
    setActiveView((cur) => (cur === name ? COVER_KEY : name));

  // Delete any automation (admin only): backend backs up + deletes its config,
  // disables its schedule, removes it from the sidebar. Restorable from
  // Admin → Automations → Deleted automations.
  const [deletingTask, setDeletingTask] = useState(null);
  const handleDeleteTask = async (name, label) => {
    const ok = window.confirm(
      `Delete "${label}"?\n\nThis removes it from the sidebar, deletes its config and stops its schedule. ` +
        "You can restore it later from Admin → Automations → Deleted automations."
    );
    if (!ok) return;
    setDeletingTask(name);
    try {
      await deleteAutomation(name);
      if (activeView === name) setActiveView(COVER_KEY);
      qc.invalidateQueries({ queryKey: ["automationGroups"] });
      qc.invalidateQueries({ queryKey: ["automationBootstrap"] });
      qc.removeQueries({ queryKey: ["taskConfig", name] });
    } catch (e) {
      window.alert(e?.message || "Delete failed");
    } finally {
      setDeletingTask(null);
    }
  };

  const totalRuns =
    activeTaskName && runSummary[activeTaskName]
      ? runSummary[activeTaskName].count ?? 0
      : activeTaskName
      ? 0
      : null;

  const canEditActive = activeTaskName ? perms.canEditTask(activeTaskName) : false;

  const toggleTheme = () => setTheme((p) => (p === "light" ? "dark" : "light"));
  const toggleSidebar = () => setSidebarOpen((p) => !p);

  return (
    <div className={`function-page ${theme}`}>
      <Navbar
        theme={theme}
        toggleTheme={toggleTheme}
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        function_name="Automation"
        userLogin={user?.userId || "Guest"}
        userRole={perms.role}
        showAdminButton={perms.canAdmin}
        onAdminClick={showAdmin}
        isAdminActive={onAdmin}
      />

      <div className="function-page__body">
        <AutomationAccordionSidebar
          isOpen={sidebarOpen}
          sections={visibleSections}
          coverActive={onCover}
          activeTaskName={activeTaskName}
          onSelectCover={showCover}
          onSelectTask={openTask}
          canDelete={perms.canDelete}
          onDeleteTask={handleDeleteTask}
          deletingTask={deletingTask}
          showActivity={perms.canViewActivity}
          activityActive={onActivity}
          onSelectActivity={showActivity}
        />

        <main
          className={`function-page__main auto-main ${
            sidebarOpen ? "function-page__main--sidebar-open" : ""
          }`}
        >
          {onAdmin ? (
            <AutomationAdmin
              sections={sections}
              canAdmin={perms.canAdmin}
              canManageUsers={perms.canManageUsers}
              role={perms.role}
              isOwner={perms.isOwner}
              perms={perms}
            />
          ) : onActivity ? (
            <ActivityLogPage />
          ) : onCover ? (
            <AutomationCover
              sections={visibleSections}
              loading={sectionsLoading}
              onOpenTask={openTask}
              isAdmin={perms.isAdmin}
            />
          ) : (
            <AutomationTaskView
              taskName={activeTaskName}
              label={activeTaskLabel}
              config={taskConfig}
              isLoading={configLoading}
              totalRuns={totalRuns}
              canEdit={canEditActive}
              isAdmin={perms.isAdmin}
              userLogin={user?.userId || ""}
            />
          )}
        </main>
      </div>

      <Footer theme={theme} />
    </div>
  );
}
